import { useEffect, useState } from "react";
import type { DialogueAction } from "@/hooks/useDialogueQorum";
import type { TextSelectionState } from "@/hooks/useTextSelection";

interface Props {
  selection: TextSelectionState | null;
  onAction: (action: DialogueAction) => void;
}

const ACTIONS: { slug: DialogueAction; label: string }[] = [
  { slug: "approfondir", label: "Approfondir" },
  { slug: "expliquer", label: "Expliquer" },
  { slug: "verifier", label: "Vérifier" },
  { slug: "non_pertinent", label: "Non pertinent" },
];

export function StabiloToolbar({ selection, onAction }: Props) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (selection) {
      // Trigger CSS transition next frame
      const id = requestAnimationFrame(() => setMounted(true));
      return () => cancelAnimationFrame(id);
    }
    setMounted(false);
  }, [selection]);

  if (!selection) return null;

  const { rect } = selection;
  // Position above the selection. Coordinates from getBoundingClientRect are
  // viewport-relative, so we use `position: fixed` (no scroll offset needed).
  const top = Math.max(8, rect.top - 52);
  const left = Math.max(
    8,
    Math.min(
      window.innerWidth - 8,
      rect.left + rect.width / 2,
    ),
  );

  return (
    <div
      data-testid="stabilo-toolbar"
      role="toolbar"
      aria-label="Dialogue sur le passage sélectionné"
      style={{
        position: "fixed",
        top,
        left,
        transform: `translateX(-50%) translateY(${mounted ? 0 : 6}px)`,
        opacity: mounted ? 1 : 0,
        transition: "opacity .18s ease, transform .18s ease",
        zIndex: 60,
        background: "var(--paper, #FBF8F1)",
        border: "0.5px solid var(--leather, #FF1744)",
        borderRadius: 999,
        boxShadow: "0 10px 30px rgba(20,16,12,0.18)",
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: "4px 6px",
      }}
      onMouseDown={(e) => {
        // Prevent the toolbar click from clearing the native selection
        // before we capture the action.
        e.preventDefault();
      }}
    >
      {ACTIONS.map((a) => (
        <button
          key={a.slug}
          type="button"
          data-testid={`stabilo-action-${a.slug}`}
          onClick={() => onAction(a.slug)}
          style={{
            fontFamily: '"DM Mono", monospace',
            fontWeight: 500,
            fontSize: 11,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: "var(--charcoal, #1A1714)",
            background: "transparent",
            border: "none",
            borderRadius: 999,
            padding: "8px 14px",
            cursor: "pointer",
            transition: "background .15s ease, color .15s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "var(--gold-tint, #F5EDD8)";
            e.currentTarget.style.color = "var(--leather-deep, #4A0F1C)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = "var(--charcoal, #1A1714)";
          }}
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

export default StabiloToolbar;
